/**
 * Dark Mode Schedule Utility
 * Automatically switches between light and dark themes based on time of day
 * 
 * Schedule:
 * - 6 PM (18:00) to 6 AM (06:00): Dark mode
 * - 6 AM to 6 PM: Light mode
 */

const DARK_MODE_START = 18; // 6 PM
const DARK_MODE_END = 6;    // 6 AM

/**
 * Determines if dark mode should be active based on current time
 * @returns {boolean} true if dark mode should be active, false for light mode
 */
export const shouldUseDarkMode = () => {
  const now = new Date();
  const currentHour = now.getHours();

  // Dark mode from 6 PM to 6 AM (midnight to 6 AM, and 6 PM to midnight)
  if (currentHour >= DARK_MODE_START || currentHour < DARK_MODE_END) {
    return true;
  }
  return false;
};

/**
 * Gets the current theme based on schedule and user preference
 * @param {string} userPreference - User's saved theme preference ('light', 'dark', or 'auto')
 * @returns {string} 'light' or 'dark'
 */
export const getScheduledTheme = (userPreference = 'auto') => {
  if (userPreference === 'auto') {
    return shouldUseDarkMode() ? 'dark' : 'light';
  }
  return userPreference;
};

/**
 * Calculates milliseconds until next theme switch
 * @returns {number} milliseconds until next switch
 */
export const getTimeUntilNextSwitch = () => {
  const now = new Date();
  const currentHour = now.getHours();

  let nextSwitchHour;
  if (currentHour >= DARK_MODE_START) {
    // Next switch is at 6 AM tomorrow
    nextSwitchHour = DARK_MODE_END + 24;
  } else if (currentHour >= DARK_MODE_END) {
    // Next switch is at 6 PM today
    nextSwitchHour = DARK_MODE_START;
  } else {
    // Before 6 AM, next switch is at 6 PM
    nextSwitchHour = DARK_MODE_START;
  }

  const nextSwitch = new Date();
  nextSwitch.setHours(nextSwitchHour, 0, 0, 0);

  if (nextSwitch <= now) {
    nextSwitch.setDate(nextSwitch.getDate() + 1);
  }

  return nextSwitch.getTime() - now.getTime();
};

/**
 * Gets a human-readable string for next theme switch time
 * @returns {string} e.g. "Dark mode in 2 hours 30 minutes"
 */
export const getNextSwitchTimeString = () => {
  const ms = getTimeUntilNextSwitch();
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const isDarkNext = shouldUseDarkMode();
  const nextMode = isDarkNext ? 'light mode' : 'dark mode';

  if (hours > 0) {
    return `${nextMode} in ${hours}h ${minutes}m`;
  }
  return `${nextMode} in ${minutes}m`;
};
